import type { FC, ReactNode } from 'react'

import Link from 'next/link'

export type ButtonVariant = 'style-01' | 'style-02' | 'style-03' | 'style-04'

type ButtonProps = {
    href: string
    label: string
    variant?: ButtonVariant
    className?: string
    /** Opts the button into the on-load hero entrance. */
    heroReveal?: boolean
}

/**
 * The original build carried each button style as a component variant class.
 * Those class names are preserved verbatim so the ported stylesheet keeps
 * matching them exactly.
 */
const VARIANT_CLASS: Record<ButtonVariant, string> = {
    'style-01': '',
    'style-02': 'w-variant-5f5e2208-a6d6-ad0f-9c0e-f800bcf96b56',
    'style-03': 'w-variant-45174e52-118f-361a-b25b-a9a833a202e3',
    'style-04': 'w-variant-da52bc0d-8b2b-c6b3-af1a-095b90382691'
}

const CONTENT_VARIANTS: ButtonVariant[] = ['style-03', 'style-04']

const join = (...values: (string | false | undefined)[]): string =>
    values.filter(Boolean).join(' ')

export const Button: FC<ButtonProps> = ({
    href,
    label,
    variant = 'style-01',
    className,
    heroReveal
}): ReactNode => {
    const variantClass = VARIANT_CLASS[variant]
    const contentClass = CONTENT_VARIANTS.includes(variant) ? variantClass : ''

    return (
        <Link
            href={href}
            className={join('button', variantClass, 'w-inline-block', className)}
            {...(heroReveal ? { 'data-hero-reveal': '' } : {})}
        >
            <div className={join('button-content', contentClass)}>
                <p className={join('button-text', variantClass, 'normal')}>
                    {label}
                </p>

                <p className={join('button-text', variantClass, 'hover')} aria-hidden='true'>
                    {label}
                </p>
            </div>
        </Link>
    )
}
